import moment, { Moment } from 'moment';
import CryptoJS from 'crypto-js';
import { enUS } from 'date-fns/locale';
import {
  ApiError,
  GamingNonGamingTotalValue,
  PlayerStatus,
  PlayerApiKey,
  PlayerChartItem,
  ResetPasswordQuery,
  StorageKey,
  ParsedVisitGamblingActivity,
  VisitActivityType,
  ParsedATMVisit,
  OrgUnit,
} from './helper.type';
import {
  CustomerAtmUseSummary,
  HarmObserved,
  HarmReport,
  IntervalCount,
  InterventionReport,
  Location,
  Organization,
  OrganizationUnit,
  OrganizationUnitType,
  PlayerSummary,
  SubOrganization,
  SweepStatus,
  UserStatus,
  UserType,
  Venue,
  VenueBranch,
  VisitActivity,
  VisitType,
} from '@inplay/models';
import { ReactNode, RefObject, useEffect } from 'react';
import {
  Description,
  SummaryProps,
} from '../component/summaryDescription/summaryDescription.type';
import { jwtDecode } from 'jwt-decode';
import { AuthTokenResponse, VenueUnitDetails } from '../service/service.type';
import { SortKey } from '../component/table/table.type';
import {
  sortByName,
  sortByStatus,
  sortByUserType,
} from '../pages/userManagement/userManagementHelper';
import { UserKey } from '../pages/userManagement/userManagement.type';
import {
  GroupedOrganizationUnit,
  GroupedOrganizationType,
  GroupedSubOrganizationType,
  GroupedVenueType,
  GroupedBranchType,
} from '../service/context/organizationContext.types';
import {
  entries,
  isArray,
  isString,
  isUndefined,
  some,
  startCase,
} from 'lodash';
import { Option } from '../component/dropDownInput/dropDownInput.type';
import { getOrganizationUnitsByTypeAndParent } from '../pages/userManagement/venuesView/venuesViewHelper';
import { DateRange, Locale } from 'react-day-picker';
import { refreshAccessToken } from './apiClient';
import { Id, toast } from 'react-toastify';
import SignatureCanvas from 'react-signature-canvas';
import { SignaturePadRef } from '../component/signaturePad/signaturePad.type';

export const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'status' in error
  );
};

export const errorToast = (error: unknown): Id | undefined => {
  if (isApiError(error) && '_skipErrorToast' in error && error._skipErrorToast)
    return;
  return toast.error(
    isApiError(error)
      ? error.response?.data?.message
      : 'An unexpected error occurred',
  );
};

export const maxDate: Moment = moment(new Date());

export const minDate: Moment = moment('2024-01-01');

export const formatTimestampToTime = (timestamp: Date | string): string => {
  return moment(timestamp).format('hh:mm A');
};

/**
 * @description Converts a given number of minutes into a compact, readable string format.
 * @param {number} minutes - Total minutes to be converted.
 * @returns {string} A string representation in the format "Xh Ym" where X is hours and Y is the remaining minutes.
 *
 * @example
 * convertMinutesToCompactFormat(135); // "2h 15m"
 * convertMinutesToCompactFormat(45);  // "0h 45m"
 */
export const convertMinutesToCompactFormat = (
  minutes: number | string,
): string => {
  try {
    const hours: number = Math.floor(+minutes / 60);
    const remainingMinutes: number = +minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  } catch (error: unknown) {
    console.error(error);
    throw error;
  }
};

/**
 * @description Converts a given date string into a 12-hour AM/PM format.
 *
 * @param {string} dateString - A string representing a date in a format parseable by the Date constructor.
 * @returns {string} The formatted time in "h:mm AM/PM" format.
 *
 * @example
 * convertToAMPM("2023-10-29T14:30:00"); // "2:30 PM"
 * convertToAMPM("2023-10-29T09:05:00"); // "9:05 AM"
 */
export const convertToAMPM = (dateString: Date): string => {
  const date: Date = new Date(dateString);

  let hours: number | string = date.getHours();
  let minutes: number | string = date.getMinutes();

  const period: string = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${hours}:${minutes} ${period}`;
};

/**
 * @description Calculates the time difference between two date strings and returns a formatted string representation.
 *
 * @param {string} start - The start date and time in a format parseable by the Date constructor.
 * @param {string} end - The end date and time in a format parseable by the Date constructor.
 * @returns {string} The difference between the two times in "Xh Ym" format if hours are present or "Ym" if only minutes are present.
 *
 * @example
 * getTimeDifference("2023-10-29T08:00:00", "2023-10-29T10:45:00"); // "2h 45m"
 * getTimeDifference("2023-10-29T08:00:00", "2023-10-29T08:30:00"); // "30m"
 */
export const getTimeDifference = (start: Date, end: Date): string => {
  const startTime: Date = new Date(start);
  const endTime: Date = new Date(end);

  const diffMs: number = endTime.getTime() - startTime.getTime();

  const minutes: number = Math.floor((diffMs / 1000 / 60) % 60);
  const hours: number = Math.floor(diffMs / 1000 / 60 / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export const calculateHarmCounts = (
  players: (PlayerSummary | CustomerAtmUseSummary)[],
): number => {
  return players.reduce(
    (
      harmCount: number,
      playerDetail: PlayerSummary | CustomerAtmUseSummary,
    ) => {
      if (
        PlayerApiKey.HarmObserved in playerDetail &&
        playerDetail.harmObserved === HarmObserved.YES
      ) {
        harmCount++;
      }
      return harmCount;
    },
    0,
  );
};

/**
 * Given a selected date, this function returns the start and end of the day as Unix timestamps.
 *
 * @param selectedDate - The date for which the start and end of the day need to be calculated.
 *                         If no date is provided, it returns undefined for both startTime and endTime.
 *
 * @returns An object containing the `startTime` and `endTime` as Unix timestamps (strings),
 *          or undefined if no `selectedDate` is provided.
 */
const getStartAndEndOfDay = (
  selectedDate?: Date,
): { startTime?: string; endTime?: string } => {
  if (!selectedDate) return { startTime: undefined, endTime: undefined };

  const startTime = moment(selectedDate).startOf('day').unix().toString();
  const endTime = moment(selectedDate).endOf('day').unix().toString();

  return { startTime, endTime };
};

export default getStartAndEndOfDay;

/**
 * Converts an array of intervals with start time and player count into a chart-friendly format.
 * The chart data will consist of formatted time (AM/PM) and the corresponding number of players.
 *
 * This function also sorts the data by time in ascending order, first sorting AM data,
 * then PM data, to ensure the time sequence is correct.
 *
 * @param intervals - An array of intervals, where each interval contains a start time and player count.
 * @returns An array of objects, each containing a formatted time and the number of players for that time.
 */
export const formatIntervalsForPlayerChart = (
  intervals: IntervalCount[],
): { time: string; players: number }[] => {
  const playerChartItems: PlayerChartItem[] = intervals.map(
    (interval: IntervalCount) => ({
      time: moment(interval.intervalStart).format('h A'),
      players: interval.playerCount,
    }),
  );

  const amData: PlayerChartItem[] = playerChartItems.filter(
    (data: PlayerChartItem) => data.time.includes('AM'),
  );
  const pmData: PlayerChartItem[] = playerChartItems.filter(
    (data: PlayerChartItem) => data.time.includes('PM'),
  );

  amData.sort(
    (a, b) => moment(a.time, 'h A').hour() - moment(b.time, 'h A').hour(),
  );
  pmData.sort(
    (a, b) => moment(a.time, 'h A').hour() - moment(b.time, 'h A').hour(),
  );

  return [...amData, ...pmData];
};

/**
 * Calculates the average time players spent in the gaming room.
 *
 * This function takes an array of player data, sums up the total gaming time,
 * and computes the average time spent per player. The result is formatted in
 * "Hh Mm" or "Mm" format.
 *
 * @param { PlayerSummary | CustomerAtmUseSummary[]} players - An array of player data containing gaming time in minutes.
 * @returns {string} - The average gaming time formatted as "Xh Ym" or "Xm" if less than an hour.
 *
 * @example
 * const players = [
 *   { id: 1, minutesInGamingRoom: 120 },
 *   { id: 2, minutesInGamingRoom: 80 },
 *   { id: 3, minutesInGamingRoom: 100 }
 * ];
 *
 * console.log(calculateAverageGamingTime(players)); // "1h 20m"
 */
export const calculateAverageGamingTime = (
  players: PlayerSummary[],
): string => {
  try {
    if (players.length === 0) {
      return '0h 00m';
    }

    const totalMinutes = players.reduce(
      (acc: number, player: PlayerSummary) => {
        if (PlayerApiKey.GamingInMinute in player) {
          return acc + +player.minutesInGamingRoom;
        }
        return acc;
      },
      0,
    );
    const averageMinutes = Math.floor(totalMinutes / players.length);
    const hours = Math.floor(averageMinutes / 60);
    const minutes = averageMinutes % 60;

    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  } catch (error: unknown) {
    console.error(error);
    throw error;
  }
};

/**
 * Decrypts an encrypted image string using AES encryption algorithm and a secret key.
 *
 * @param {string} encryptedImage - The encrypted image string in Base64 format.
 * @param {string} secretKey - The secret key used for AES decryption.
 *
 * @returns {string} - The decrypted image as a Base64 string. If decryption fails, an empty string is returned.
 */
export const decryptImage = (
  encryptedImage: string,
  secretKey: string,
): string => {
  try {
    const bytes: CryptoJS.lib.WordArray = CryptoJS.AES.decrypt(
      encryptedImage,
      secretKey,
    );
    const decryptedBase64: string = bytes.toString(CryptoJS.enc.Base64);
    return decryptedBase64;
  } catch (error: unknown) {
    console.error('Decryption failed:', error);
    if (error instanceof Error) {
      throw new Error(`Decryption failed: ${error.message}`);
    } else {
      throw new Error('Decryption failed: unknown error');
    }
  }
};

/**
 * Generates a random secret key for encryption or decryption using a 32-byte cryptographically secure random value.
 *
 * @returns {string} - A randomly generated secret key in Base64 format.
 */
export const generateSecretKey = (): string => {
  const randomKey: CryptoJS.lib.WordArray = CryptoJS.lib.WordArray.random(32);
  return randomKey.toString(CryptoJS.enc.Base64);
};

/**
 * Calculates the total gaming and non-gaming usage counts from an array of customer ATM use summaries.
 *
 * This function iterates through the provided data array of `CustomerAtmUseSummary` objects, summing up
 * the `gamingUseCount` and `nonGamingUseCount` for each player.
 *
 * @param data - An array of `CustomerAtmUseSummary` objects representing the data for each player.
 * @returns An object of type `GamingNonGamingData` containing the total gaming and non-gaming usage counts.
 *
 * Example usage:
 * const totals = calculateTotalsGamingNonGaming(data);
 * console.log(totals.totalGaming); // Sum of all gaming usage counts
 * console.log(totals.totalNonGaming); // Sum of all non-gaming usage counts
 */
export const calculateTotalsGamingNonGaming = (
  data: CustomerAtmUseSummary[],
): GamingNonGamingTotalValue => {
  // Calculate the total gaming usage count by summing up the gamingUseCount for each player
  const totalGaming: number = data.reduce(
    (sum, player) => sum + player.gamingUseCount,
    0,
  );

  // Calculate the total non-gaming usage count by summing up the nonGamingUseCount for each player
  const totalNonGaming: number = data.reduce(
    (sum, player) => sum + player.nonGamingUseCount,
    0,
  );

  // Return the totals as an object
  return { totalGaming, totalNonGaming };
};

/**
 * Calculates the percentage of total gaming and non-gaming withdrawals based on the total withdrawals.
 *
 * @param totalGaming - The total amount of gaming-related withdrawals.
 * @param totalNonGaming - The total amount of non-gaming-related withdrawals.
 * @param totalWithdrawals - The total amount of all withdrawals (gaming + non-gaming).
 *
 * @returns An object containing the percentage of gaming and non-gaming withdrawals, rounded to two decimal places.
 *          If totalWithdrawals is 0, the function returns 0 for both percentages.
 */
export const calculateGamingNonGamingPercentage = (
  totalGaming: number,
  totalNonGaming: number,
  totalWithdrawals: number,
): GamingNonGamingTotalValue => {
  // If totalWithdrawals is zero, return 0% for both gaming and non-gaming percentages
  if (totalWithdrawals === 0) {
    return { totalGaming: 0, totalNonGaming: 0 };
  }

  // Calculate the percentage of gaming withdrawals out of total withdrawals
  const gamingPercentage: number = (totalGaming / totalWithdrawals) * 100;

  // Calculate the percentage of non-gaming withdrawals out of total withdrawals
  const nonGamingPercentage: number = (totalNonGaming / totalWithdrawals) * 100;

  // Return the percentages rounded to two decimal places
  return {
    totalGaming: parseFloat(gamingPercentage.toFixed(2)),
    totalNonGaming: parseFloat(nonGamingPercentage.toFixed(2)),
  };
};

export const calculateTotalWithdrawals = (
  atmUseSummary: CustomerAtmUseSummary[],
): number => {
  // Use the reduce method to iterate over the array of CustomerAtmUseSummary objects.
  // For each object (representing a player's ATM usage summary), add the `totalWithdrawals` property
  // to the accumulator (`sum`). Return the accumulated sum as the final total.
  return atmUseSummary.reduce(
    (withdrawalSum: number, playerDetail: CustomerAtmUseSummary) =>
      withdrawalSum + playerDetail.totalWithdrawals,
    0,
  );
};

export const countUsersWithMoreThanTwoWithdrawals = (
  atmUseSummary: CustomerAtmUseSummary[],
): number => {
  // Filter the users who have made two or more withdrawals and return the count
  return atmUseSummary.filter(
    (player: CustomerAtmUseSummary) => player.totalWithdrawals >= 2,
  ).length;
};
/**
 * Generates chart data for gaming and non-gaming categories.
 *
 * @param totalGaming - The total value of gaming-related data to be represented on the chart.
 * @param totalNonGaming - The total value of non-gaming-related data to be represented on the chart.
 *
 * @returns An array of objects with the chart data for the "Gaming" and "Non-Gaming" categories,
 *          each containing a name and corresponding value.
 */
export const getGamingChartData = (
  totalGaming: number,
  totalNonGaming: number,
) => {
  if (totalGaming === 0 && totalNonGaming === 0) {
    return [{ name: 'No Data', value: 1 }];
  }
  return [
    { name: 'Gaming', value: totalGaming },
    { name: 'Non-Gaming', value: totalNonGaming },
  ];
};

export const setImageFormat = (image: string): string => {
  return `data:image/png;base64,${image}`;
};

export const isAtmVisitsSummary = (
  data: unknown,
): data is CustomerAtmUseSummary => {
  return !!data && typeof data === 'object' && 'totalWithdrawals' in data;
};

export const isPlayerSummary = (data: unknown): data is PlayerSummary => {
  return !!data && typeof data === 'object' && 'playerId' in data;
};

/**
 * @returns {PlayerStatus | ''} - Returns `LIVE` if the player is in the gaming room.
 *                                 Returns an empty string (`''`) otherwise.
 */
export const isPlayerLive = (
  playerDetail: PlayerSummary | CustomerAtmUseSummary,
): PlayerStatus => {
  if ('IsInGamingRoom' in playerDetail && playerDetail.IsInGamingRoom) {
    return PlayerStatus.LIVE;
  }
  return PlayerStatus.NOT_LIVE;
};

export const formatSummaryValue = (
  value: string | number | Date,
): ReactNode => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
};

export const calculateGamingPercentage = (
  playerDetail: PlayerSummary | CustomerAtmUseSummary,
): GamingNonGamingTotalValue => {
  const totalGaming: number =
    Number(
      playerDetail[PlayerApiKey.GamingUseCount as keyof typeof playerDetail],
    ) || 0;
  const totalNonGaming: number =
    Number(
      playerDetail[PlayerApiKey.NonGamingUseCount as keyof typeof playerDetail],
    ) || 0;
  const totalWithdrawals: number =
    Number(
      playerDetail[PlayerApiKey.TotalWithdraw as keyof typeof playerDetail],
    ) || 0;

  return calculateGamingNonGamingPercentage(
    totalGaming,
    totalNonGaming,
    totalWithdrawals,
  );
};

export const shouldHighlightPlayerSummaryItem = (
  playerSummaryDetail: SummaryProps,
  summaryList: SummaryProps[],
  totalTimeKey: string,
): boolean => {
  if (!playerSummaryDetail || !summaryList.length) return false;

  const harmObservedRow: SummaryProps | undefined = summaryList.find(
    (summaryItem: SummaryProps) =>
      summaryItem.label === Description.HarmObserve,
  );

  const isHarmObserved: boolean = harmObservedRow?.value === HarmObserved.YES;
  const isTotalTimeRow: boolean = playerSummaryDetail.label === totalTimeKey;
  const isHarmObservedRow: boolean =
    playerSummaryDetail.label === Description.HarmObserve;

  return (isTotalTimeRow || isHarmObservedRow) && isHarmObserved;
};

const safeRefresh = async (): Promise<void> => {
  try {
    await refreshAccessToken();
  } catch (error: unknown) {
    console.error(`[safeRefresh Error caught`, { error });
  }
};

/**
 * Retrieves and decodes the authentication token from local storage.
 *
 * @returns {AuthTokenPayload | null} The decoded authentication token data if valid, otherwise `null`.
 *
 * - If the token is missing, logs an error and returns `null`.
 * - If the token is invalid or expired, it is removed from local storage,
 *   and the user is redirected to the login page.
 */
export const validateAndDecodeAuthToken = (): AuthTokenResponse | null => {
  const token: string | null = localStorage.getItem(StorageKey.ACCESS_TOKEN);

  if (!token) {
    console.log(`[Token] No auth token found...`);
    return null;
  }

  try {
    const decodedToken: AuthTokenResponse | null =
      jwtDecode<AuthTokenResponse>(token);
    const expiryTimeString: string | null = localStorage.getItem(
      StorageKey.EXPIRY_TIME,
    );

    const expiryTime: number = expiryTimeString ? Number(expiryTimeString) : 0;

    const currentTimeSecond: number = Math.floor(Math.floor(Date.now() / 1000));

    if (
      expiryTime &&
      expiryTime < currentTimeSecond &&
      decodedToken?.type === UserType.VENUE_BRANCH_LOGIN
    ) {
      console.log(
        `[validateAndDecodeAuthToken][Staff PIN session has expired]`,
      );

      // Redirect to login page
      localStorage.removeItem(StorageKey.ACCESS_TOKEN);
      localStorage.removeItem(StorageKey.REFRESH_TOKEN);
      localStorage.removeItem(StorageKey.EXPIRY_TIME);
      localStorage.removeItem(StorageKey.STAFF_PIN);
      localStorage.removeItem(StorageKey.VENUE_UNIT_DETAILS);
      window.location.href = '/login';

      return null;
    }

    if (decodedToken.exp && decodedToken.exp < currentTimeSecond) {
      safeRefresh();
    }

    return decodedToken;
  } catch (error: unknown) {
    console.error(`[Expired Token] Error`, { error });
    localStorage.removeItem(StorageKey.ACCESS_TOKEN);
    localStorage.removeItem(StorageKey.EXPIRY_TIME);
    localStorage.removeItem(StorageKey.VENUE_UNIT_DETAILS);
    window.location.href = '/login';
    return null;
  } finally {
    console.log(`[Token Validation] Process completed.`);
  }
};

/**
 * Safely extracts and validates password reset parameters from URL query string.
 *
 * @returns {ResetPasswordQuery} Sanitized parameters or null values if invalid
 */
export const getPasswordParams = (): ResetPasswordQuery => {
  try {
    const searchParams = window.location.search.substring(1);
    if (!searchParams) {
      return { code: null, email: null };
    }
    const params: URLSearchParams = new URLSearchParams(searchParams);
    const rawCode: string = params.get('code') ?? '';
    const emailMatch: string | undefined =
      searchParams.match(/(?:email)=([^&]*)/)?.[1];
    let emailParam: string | null = null;
    if (emailMatch) {
      try {
        const decoded: string = decodeURIComponent(emailMatch);
        emailParam = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(decoded)
          ? decoded
          : null;
      } catch {
        emailParam = null;
      }
    }
    console.log(
      { code: rawCode, email: emailParam },
      `[getPasswordParams] Extracted parameters`,
    );
    return { code: rawCode, email: emailParam };
  } catch {
    return { code: null, email: null };
  }
};

/* 
    This regex ensures:
        At least one uppercase letter: (?=.*[A-Z])
        At least one lowercase letter: (?=.*[a-z])
        At least one digit: (?=.*\d)
        At least one special character from the specified set or a non-leading, non-trailing space: (?=.*[^\w\s])
        Minimum length of 8 characters: (?=.{8,})
        No leading space: (?!^\s)
        No trailing space: (?!.*\s$)
*/
export const validatePassword = (password: string) => {
  const passwordRegex =
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^\w\s])(?=.{8,})(?!^\s)(?!.*\s$).+$/;
  return passwordRegex.test(password);
};

export const getStatusDotColor = (status: UserStatus): string => {
  switch (status.toLowerCase()) {
    case UserStatus.ACTIVE:
      return 'bg-[#AEF21E]';
    case UserStatus.INVITED:
      return 'bg-[#E6A901]';
    case UserStatus.INACTIVE:
      return 'bg-[#A0A0A0]';
    default:
      return 'bg-gray-400';
  }
};

export const getSweepStatusDotColor = (status: SweepStatus): string => {
  switch (status.toLowerCase()) {
    case SweepStatus.Completed:
      return 'bg-[#AFF21C]';
    case SweepStatus.Missed:
      return 'bg-[#B1B1B1]';
    default:
      return 'bg-gray-400';
  }
};

/**
 * Sorts an array of generic objects based on a specified key and sort order.
 *
 * This function supports custom sorting logic for specific keys such as `name`, `status`, and `userType`.
 * For other string and number fields, it falls back to default sorting behavior.
 *
 * @template T - A generic type extending `Record<string,  K extends keyof T>`.
 * @param data - The array of objects to be sorted.
 * @param key - The key in each object to sort by. Must be a key of type `T`.
 * @param order - The sort direction (`ASC` for ascending or `DESC` for descending).
 * @returns A new sorted array based on the provided key and order.
 *
 * @example
 * const sorted = sortData(users, 'name', SortKey.ASC);
 */
export const sortData = <T, K extends keyof T>(
  data: T[],
  key: K,
  order: SortKey,
): T[] => {
  return [...data].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (
      key === UserKey.FIRST_NAME &&
      typeof aVal === 'string' &&
      typeof bVal === 'string'
    ) {
      return sortByName(aVal, bVal, order);
    }

    if (
      key === UserKey.STATUS &&
      typeof aVal === 'string' &&
      typeof bVal === 'string'
    ) {
      return sortByStatus(aVal as UserStatus, bVal as UserStatus, order);
    }

    if (
      key === UserKey.TYPE &&
      typeof aVal === 'string' &&
      typeof bVal === 'string'
    ) {
      return sortByUserType(aVal as UserType, bVal as UserType, order);
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortByName(aVal, bVal, order);
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === SortKey.ASC ? aVal - bVal : bVal - aVal;
    }

    return 0;
  });
};

/**
 * Custom React hook to automatically close a component when:
 *  - The user clicks outside the referenced element
 *  - The user presses the Escape key
 *
 * @param ref - A React ref object pointing to the element to monitor for outside clicks
 * @param onClose - A callback function to execute when the outside click or Escape key press is detected
 *
 * This hook attaches event listeners to the document for 'mousedown' and 'keydown' events.
 * When triggered, it checks if the click occurred outside the referenced element or if the Escape key
 * was pressed, and then calls the onClose callback to handle closing logic.
 *
 * It cleans up the event listeners automatically when the component unmounts or dependencies change.
 */
export function useCloseOnOutsideClickAndEsc(
  ref: RefObject<HTMLElement> | null,
  onClose: () => void,
) {
  useEffect(() => {
    if (!ref?.current) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        console.log('[CLICKED] outside model');
        onClose();
      }
    };

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        console.log('[ESCAPE] key pressed');
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [ref, onClose]);
}
/**
 * Returns formatted organization options for a user based on their role and organizational context.
 *
 * This function internally uses `getMatchesOrganizationOption` to fetch the relevant
 * sub-organizations, venues, and venue branches based on the user's type and organization details.
 * It then formats each of these into an `Option` type with a `label` and `value`.
 *
 * The `label` is a human-readable string using `startCase`, and the `value` is a camel-cased version
 * of the organization name (for use in dropdowns, filters, etc.).
 *
 * @param organizationSummary - A list of all available organization units in the system.
 * @param userDetail - The current user’s detail, including organization and venue info.
 * @param userType - The type of user (e.g., SUB_ORGANIZATION_ADMIN, VENUE_ADMIN, etc.).
 *
 * @returns An object containing:
 *  - `subOrganizations`: Array of formatted sub-organization options.
 *  - `venues`: Array of formatted venue options.
 *  - `venueBranches`: Array of formatted venue branch options.
 */
export const getChildOrganizationUnitOptions = (
  organizationUnits: (OrganizationUnit & { _id: string })[],
  parentOrganizationUnitId?: string,
): Option[] => {
  const parentUnit = organizationUnits.find(
    (ou) => ou._id === parentOrganizationUnitId,
  );
  const parentType = parentUnit?.type || OrganizationUnitType.ORGANIZATION;

  const filteredUnits =
    parentType === OrganizationUnitType.SUB_ORGANIZATION
      ? organizationUnits.filter(
          (ou) =>
            ou._id === parentOrganizationUnitId ||
            hasOrganizationUnit(ou, parentUnit?._id || ''),
        )
      : parentType === OrganizationUnitType.VENUE
        ? organizationUnits.filter(
            (ou) =>
              ou.type !== OrganizationUnitType.ORGANIZATION &&
              (ou._id === parentOrganizationUnitId ||
                hasOrganizationUnit(ou, parentUnit?._id || '')),
          )
        : parentType === OrganizationUnitType.VENUE_BRANCH
          ? organizationUnits.filter(
              (ou) =>
                ou.type !== OrganizationUnitType.ORGANIZATION &&
                ou.type !== OrganizationUnitType.SUB_ORGANIZATION &&
                (ou._id === parentOrganizationUnitId ||
                  hasOrganizationUnit(ou, parentUnit?._id || '')),
            )
          : organizationUnits;

  const grouped = groupOrganizationUnits(filteredUnits);

  if (!grouped) return [];

  switch (grouped.type) {
    case OrganizationUnitType.ORGANIZATION: {
      const org = grouped as GroupedOrganizationType;
      return org.suborganizations.map((unit) => ({
        label: unit.name,
        value: unit._id,
      }));
    }
    case OrganizationUnitType.SUB_ORGANIZATION: {
      const subOrg = grouped as GroupedSubOrganizationType;
      return subOrg.venues.map((venue) => ({
        label: venue.name,
        value: venue._id,
      }));
    }
    case OrganizationUnitType.VENUE: {
      const venue = grouped as GroupedVenueType;
      return venue.venueBranches.map((branch) => ({
        label: venue.name,
        value: branch._id,
      }));
    }
    case OrganizationUnitType.VENUE_BRANCH: {
      const venueBranch = grouped as GroupedBranchType;
      return [
        {
          label: venueBranch.name,
          value: venueBranch._id,
        },
      ];
    }
    default:
      return [];
  }
};

export const filterAndConvertOrgUnitsToOption = (
  orgUnits: OrgUnit[],
  type: OrganizationUnitType = OrganizationUnitType.ORGANIZATION,
  parentId?: string,
): Option[] => {
  const filteredUnits: OrgUnit[] = getOrganizationUnitsByTypeAndParent(
    orgUnits,
    type,
    parentId ?? '',
  ) as OrgUnit[];
  return filteredUnits.map((unit) => ({
    label: startCase(unit.name),
    value: unit._id,
  }));
};

export const hasOrganizationUnit = (
  organizationUnit:
    | GroupedOrganizationUnit
    | (OrganizationUnit & { _id: string }),
  parentOrganizationUnitId: string,
): boolean => {
  return some(entries(organizationUnit), ([key, value]) => {
    console.log(`[KEY CHECK]`, { key, value, parentOrganizationUnitId });

    if (isString(value)) {
      return value === parentOrganizationUnitId;
    }

    if (isArray(value)) {
      // Handle both string arrays and object arrays
      return value.some((item) => {
        if (isString(item)) {
          return item === parentOrganizationUnitId;
        }
        return false;
      });
    }
    return false;
  });
};

/**
 * Takes an array of organization units and groups them into a nested structure
 * suitable for the OrganizationContext.
 *
 * The grouping is done as follows:
 *   - Organizations are grouped into a single GroupedOrganizationType object.
 *     This object contains the organization's data, as well as a list of its associated
 *     sub-organizations.
 *   - Sub-organizations are grouped into a single GroupedSubOrganizationType object.
 *     This object contains the sub-organization's data, as well as a list of its associated
 *     venues.
 *   - Venues are grouped into a single GroupedVenueType object. This object contains the
 *     venue's data, as well as a list of its associated venue branches.
 *   - Venue branches are not grouped, but rather associated with their parent venue.
 *
 * @param units The array of organization units to group
 * @returns A GroupedOrganizationUnit object or undefined if the input array is empty
 */
export const groupOrganizationUnits = (
  organizationUnits: OrganizationUnit[],
  parentOrganizationGroupId?: string,
): GroupedOrganizationUnit | undefined => {
  if (organizationUnits.length === 0) {
    return undefined;
  }

  const organizations = organizationUnits.filter(
    (
      organizationUnit: OrganizationUnit,
    ): organizationUnit is Organization & { _id: string } =>
      organizationUnit.type === OrganizationUnitType.ORGANIZATION,
  );

  const subOrganizations = organizationUnits.filter(
    (
      organizationUnit: OrganizationUnit,
    ): organizationUnit is SubOrganization & { _id: string } =>
      organizationUnit.type === OrganizationUnitType.SUB_ORGANIZATION,
  );

  const venues = organizationUnits.filter(
    (
      organizationUnit: OrganizationUnit,
    ): organizationUnit is Venue & { _id: string } =>
      organizationUnit.type === OrganizationUnitType.VENUE,
  );

  const venueBranches = organizationUnits.filter(
    (
      organizationUnit: OrganizationUnit,
    ): organizationUnit is VenueBranch & { _id: string } =>
      !isUndefined(parentOrganizationGroupId)
        ? organizationUnit.type === OrganizationUnitType.VENUE_BRANCH &&
          organizationUnit.venueId === parentOrganizationGroupId
        : organizationUnit.type === OrganizationUnitType.VENUE_BRANCH,
  );

  /**
   * Takes a venue and returns a grouped venue with its associated venue branches
   * @param venue The venue to group
   * @returns A GroupedVenueType object
   */
  const getVenueWithBranches = (
    venue: Venue & { _id: string },
  ): GroupedVenueType => ({
    ...venue,
    venueBranches: venueBranches.filter((vb) => vb.venueId === venue._id),
  });

  /**
   * Takes a sub-organization and returns a grouped sub-organization with its associated venues
   * @param subOrg The sub-organization to group
   * @returns A GroupedSubOrganizationType object
   */
  const getSubOrgWithVenues = (
    subOrg: SubOrganization & { _id: string },
  ): GroupedSubOrganizationType => ({
    ...subOrg,
    venues: venues
      .filter((venue) => venue.subOrganizationIds.includes(subOrg._id))
      .map(getVenueWithBranches),
  });

  if (organizations.length > 0) {
    const primaryOrg = organizations[0];
    const groupedOrganization: GroupedOrganizationType = {
      ...primaryOrg,
      suborganizations: subOrganizations
        .filter((subOrg) => subOrg.organizationId === primaryOrg._id)
        .map(getSubOrgWithVenues),
    };
    return groupedOrganization;
  }

  if (subOrganizations.length > 0) {
    const primarySubOrg = subOrganizations[0];
    const groupedSubOrg: GroupedSubOrganizationType =
      getSubOrgWithVenues(primarySubOrg);
    return groupedSubOrg;
  }

  if (venues.length > 0) {
    const primaryVenue = venues[0];
    const groupedVenue: GroupedVenueType = getVenueWithBranches(primaryVenue);
    return groupedVenue;
  }
  if (venueBranches.length > 0) {
    const groupedVenueBranch: GroupedBranchType = {
      ...venueBranches[0],
      type: OrganizationUnitType.VENUE_BRANCH,
    };
    return groupedVenueBranch;
  }

  return undefined;
};

export const decodeAndFormatPlayerDescription = (input: string) => {
  if (input === '' || input === undefined || input === null) return '';
  return input
    .split(',')
    .map((pair) => {
      const [key, value] = pair.split(':');
      const formattedKey = key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
      return `${formattedKey}: ${formattedValue}`;
    })
    .join(', ');
};

// Check if venue setup is needed
export const checkVenueSetupNeeded = (
  venueUnitDetails: VenueUnitDetails,
): boolean => {
  try {
    if (!venueUnitDetails) return false;

    if (
      !venueUnitDetails.venueBranches ||
      !Array.isArray(venueUnitDetails.venueBranches) ||
      venueUnitDetails.venueBranches.length === 0
    )
      return false;

    // Check if there are any branches that need universal login setup
    return venueUnitDetails.venueBranches.some(
      (branch) => branch.hasUniversalLogin === false,
    );
  } catch (error) {
    console.error('Error checking venue setup:', error);
    return false;
  }
};

export const parseVisitActivityGamblingData = (
  visits: VisitActivity[],
): ParsedVisitGamblingActivity[] => {
  const MINIBREAK_THRESHOLD_MINUTES = 3;
  const result: ParsedVisitGamblingActivity[] = [];
  const now = new Date();

  let currentSession: {
    startTime: Date;
    endTime: Date;
    miniBreaks: number[];
    startTimeRaw: string | Date;
    endTimeRaw: string | Date | null;
  } | null = null;

  for (let i = 0; i < visits.length; i++) {
    const current = visits[i];
    const start = new Date(current.startTime);
    const end = current.endTime ? new Date(current.endTime) : now;
    const endRaw = current.endTime ?? null;

    if (currentSession) {
      const breakMillis = start.getTime() - currentSession.endTime.getTime();
      const breakMinutes = breakMillis / 60000;

      if (breakMinutes < MINIBREAK_THRESHOLD_MINUTES && breakMinutes >= 0) {
        // Merge into existing session
        currentSession.endTime = end;
        currentSession.endTimeRaw = endRaw;
        currentSession.miniBreaks.push(Math.round(breakMillis / 1000)); // seconds
        continue;
      } else if (breakMinutes >= MINIBREAK_THRESHOLD_MINUTES) {
        // Push completed session
        result.push({
          type: VisitActivityType.SESSION,
          startTime: currentSession.startTimeRaw,
          endTime: currentSession.endTimeRaw,
          timeInMinutes: Math.round(
            (currentSession.endTime.getTime() -
              new Date(currentSession.startTimeRaw).getTime()) /
              60000,
          ),
          miniBreaks: currentSession.miniBreaks.length
            ? currentSession.miniBreaks
            : undefined,
          name: '', // Placeholder
        });

        // Push break
        result.push({
          type: VisitActivityType.BREAK,
          startTime: currentSession.endTimeRaw!,
          endTime: current.startTime,
          timeInMinutes: Math.round(breakMinutes),
          name: '', // Placeholder
        });

        // Start new session
        currentSession = {
          startTime: start,
          endTime: end,
          miniBreaks: [],
          startTimeRaw: current.startTime,
          endTimeRaw: endRaw,
        };
      } else {
        continue; // Invalid gap
      }
    } else {
      currentSession = {
        startTime: start,
        endTime: end,
        miniBreaks: [],
        startTimeRaw: current.startTime,
        endTimeRaw: endRaw,
      };
    }
  }

  if (currentSession) {
    const isOngoing = currentSession.endTimeRaw === null;

    result.push({
      type: VisitActivityType.SESSION,
      startTime: currentSession.startTimeRaw,
      endTime: isOngoing ? null : currentSession.endTimeRaw,
      timeInMinutes: Math.round(
        ((isOngoing ? now : currentSession.endTime).getTime() -
          new Date(currentSession.startTimeRaw).getTime()) /
          60000,
      ),
      miniBreaks: currentSession.miniBreaks.length
        ? currentSession.miniBreaks
        : undefined,
      name: '', // Placeholder
    });
  }

  // Assign names (Session 1, Break 1, ...)
  const ascending = [...result].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );

  let sessionCount = 1;
  let breakCount = 1;
  for (const item of ascending) {
    if (item.type === VisitActivityType.SESSION) {
      item.name = `Session ${sessionCount++}`;
    } else {
      item.name = `Break ${breakCount++}`;
    }
  }

  // Sort in descending order (latest to oldest)
  const finalSorted = ascending.sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
  );

  return finalSorted;
};

export const getMaskedPassword = (password: string) => {
  return '•'.repeat(password?.length || 0);
};
export const parseVisitActivityATMData = (
  visits: VisitActivity[],
): ParsedATMVisit[] => {
  let gamingVisitCount: number = 0;

  const parsed: ParsedATMVisit[] = visits
    .map((item, i) => {
      if (item.location !== Location.ATM) return null;

      const firstGamingSession: VisitActivity | undefined = visits
        .slice(i + 1)
        .find((v) => v.location === Location.GAMING);

      let name: string;
      if (item.visitType === VisitType.GAMING) {
        gamingVisitCount += 1;
        name = `${formatOrdinal(gamingVisitCount)} Gambling Use`;
      } else {
        name = 'Other Use';
      }

      return {
        visitType: item.visitType,
        location: Location.ATM,
        startTime: item.startTime,
        endTime: item.endTime,
        firstGamingSessionStartTime: firstGamingSession?.startTime ?? null,
        poiClassification: item.poiClassification,
        name,
      } as ParsedATMVisit;
    })
    .filter((v): v is ParsedATMVisit => v !== null);

  return parsed.sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
  );
};

const formatOrdinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const customLocale: Locale = {
  ...enUS,
  localize: {
    ...enUS.localize,
    day: (weekNumber: 0 | 1 | 2 | 3 | 4 | 5 | 6): string =>
      ['S', 'M', 'T', 'W', 'T', 'F', 'S'][weekNumber],
    ordinalNumber:
      enUS.localize?.ordinalNumber ??
      ((ordinalNumber: number) => `${ordinalNumber}`),
    era: enUS.localize?.era ?? ((era: number) => `${era}`),
    quarter: enUS.localize?.quarter ?? ((quarter: number) => `${quarter}`),
    month: enUS.localize?.month ?? ((month: number) => `${month}`),
    dayPeriod: enUS.localize?.dayPeriod ?? ((month: number) => `${month}`),
  },
};

/**
 * Returns the first and last available dates for a given month.
 *
 * @param date - The reference date from which the month will be extracted.
 * @param absoluteRange - If true, always returns the actual first and last day of the month,
 *                        regardless of `minDate` and `maxDate` constraints.
 * @returns A `DateRange` object with `from` and `to` representing the start and end of the month,
 *          adjusted based on availability if `absoluteRange` is false.
 */
export const getFirstAndLastDayOfMonth = (
  date: Date,
  absoluteRange?: boolean,
): DateRange => {
  const momentDate: Moment = moment(date);

  const firstDayOfMonth: Moment = momentDate.clone().startOf('month');
  const lastDayOfMonth: Moment = momentDate.clone().endOf('month');

  if (absoluteRange) {
    return {
      from: firstDayOfMonth.toDate(),
      to: lastDayOfMonth.toDate(),
    };
  }

  const earliestAvailableDateForMonth: Date = firstDayOfMonth.isBefore(minDate)
    ? minDate.toDate()
    : firstDayOfMonth.toDate();

  const lastAvailableDateForMonth: Date =
    momentDate.isSame(maxDate, 'month') && momentDate.isSame(maxDate, 'year')
      ? maxDate.toDate()
      : lastDayOfMonth.toDate();

  return {
    from: earliestAvailableDateForMonth,
    to: lastAvailableDateForMonth,
  };
};

/**
 * Clear the signature canvas
 */
export const clearSignature = (
  canvasRef: React.RefObject<SignatureCanvas | SignaturePadRef>,
) => {
  canvasRef.current?.clear();
};

/**
 * Get the trimmed signature as a base64 data URL
 */
export const getTrimmedSignature = (
  canvasRef: React.RefObject<SignatureCanvas>,
): string => {
  return canvasRef.current?.getTrimmedCanvas().toDataURL('image/png') || '';
};

/**
 * Check if the signature pad is empty
 */
export const isSignatureEmpty = (
  canvasRef: React.RefObject<SignatureCanvas>,
): boolean => {
  return canvasRef.current?.isEmpty() ?? true;
};

export const getHourRange = (
  date?: Date,
  hour?: number,
): {
  hourStartTime: string;
  hourEndTime: string;
} => {
  const now: Moment = moment();
  const inputDate: Moment = date ? moment(date) : now.clone();

  const isToday: boolean = inputDate.isSame(now, 'day');

  const resolvedHour: number =
    hour !== undefined ? hour : isToday ? now.hour() : 23;

  const hourStart: Moment = inputDate
    .clone()
    .hour(resolvedHour)
    .minute(0)
    .second(0)
    .millisecond(0);

  const hourEnd: Moment = hourStart.clone().endOf('hour');

  return {
    hourStartTime: hourStart.unix().toString(),
    hourEndTime: hourEnd.unix().toString(),
  };
};

// Convert file to base64
export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};
//Check its base 64 url or not
export const isBase64Image = (value: string | undefined): boolean =>
  !!value && value.startsWith('data:image/');

/**
 * Returns the Unix timestamps for the start and end of a selected date range.
 *
 * - If both `from` and `to` dates are provided and fall on the same day, the function returns:
 *   - `startTime` as the **start of that day** (00:00:00)
 *   - `endTime` as the **end of that day** (23:59:59)
 * - If the dates are different, it returns the exact timestamps of the provided dates.
 * - If either `from` or `to` is missing, the current date/time is used as default.
 *
 * @param range - An object containing optional `from` and `to` Date values.
 * @returns An object with `startTime` and `endTime` as Unix timestamps (in seconds).
 */
export const getStartAndEndUnixTimestamps = (
  range: DateRange | undefined,
): { startTimestamp: number; endTimestamp: number } => {
  const startDate: Moment = range?.from ? moment(range.from) : moment();
  const endDate: Moment = range?.to ? moment(range.to) : moment();

  const isSameDay: boolean = startDate.isSame(endDate, 'day');

  const startTimestamp: number = isSameDay
    ? startDate.startOf('day').unix()
    : startDate.startOf('day').unix();
  const endTimestamp: number = isSameDay
    ? endDate.endOf('day').unix()
    : endDate.endOf('day').unix();

  return { startTimestamp, endTimestamp };
};

/**
 * Downloads a PDF file from a base64-encoded string.
 *
 * @param base64String - Base64 encoded PDF string (without the data: prefix).
 * @param fileName - Desired file name (without extension).
 */
export const downloadPdfFromBase64 = (
  base64String: string,
  fileName: string,
): void => {
  // Decode base64 into binary string
  const byteCharacters: string = atob(base64String);

  const byteNumbers: number[] = new Array<number>(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray: Uint8Array = new Uint8Array(byteNumbers);

  const arrayBuffer: ArrayBuffer = byteArray.buffer as ArrayBuffer;
  const blob: Blob = new Blob([arrayBuffer], { type: 'application/pdf' });

  const url: string = URL.createObjectURL(blob);
  const link: HTMLAnchorElement = document.createElement('a');

  link.href = url;
  link.download = `${fileName}.pdf`;

  link.click();

  URL.revokeObjectURL(url);
};

export const getIdAttributes = (component: string, element: string) => {
  const value = `${component}-${element}`;
  return {
    id: value,
    'data-testid': value,
  };
};

export const getDataTestIdValue = (component: string, element: string) =>
  getIdAttributes(component, element)['data-testid'];

export const addElipsesToText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + '...';
};

export const combineDateAndTime = (
  date: Date | undefined,
  timeString: string,
): string => {
  // Value guard
  if (!date) {
    date = new Date();
  }
  // Type guard
  if (typeof date === 'string') {
    date = new Date(date);
  }
  let excessTime: number = 0;
  const [time, meridiem] = timeString.split(' ');
  if (meridiem && meridiem === 'PM') {
    excessTime = 12;
  }

  const [hours, minutes] = time.split(':').map(Number);
  date.setHours(hours + excessTime, minutes, 0, 0);
  return date.toISOString();
};

/**
 * Formats minutes into hours and minutes format (e.g., "02h 30m")
 * @param minutes - Number of minutes or string representation
 * @returns Formatted string in "XXh XXm" format
 */
export const formatMinutesToHoursAndMinutes = (
  minutes: number | string | undefined,
): string => {
  if (!minutes) return '00h 00m';
  const num = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
  if (isNaN(num)) return '00h 00m';
  const h = Math.floor(num / 60);
  const m = num % 60;
  return `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m`;
};

/**
 * Formats a date into DD/MM/YYYY, hh:mm A format
 * @param date - Date to format (Date object, string, or undefined)
 * @returns Formatted date string or empty string if invalid
 */
export const formatDateWithTime = (date: Date | string | undefined): string => {
  if (!date) return '';
  return moment(date).format('DD/MM/YYYY, hh:mm A');
};

/**
 * Formats time values for display in reports
 * @param data - Object containing time data
 * @param t - Translation function
 * @returns Formatted time values object
 */
export const getFormattedTimeValues = (
  data: {
    uninterruptedTime?: number | string;
    totalTimeInGameArea?: number | string;
    totalTimeOutsideGameArea?: number | string;
  },
  t: (key: string) => string,
) => ({
  currentUninterruptedTime: data.uninterruptedTime
    ? formatMinutesToHoursAndMinutes(data.uninterruptedTime) +
      ` - ${t('createHarmReport.ongoing')}`
    : `00h 00m - ${t('createHarmReport.ongoing')}`,
  totalTimeInGambling: data.totalTimeInGameArea
    ? formatMinutesToHoursAndMinutes(data.totalTimeInGameArea)
    : '00h 00m',
  totalTimeOutside: data.totalTimeOutsideGameArea
    ? formatMinutesToHoursAndMinutes(data.totalTimeOutsideGameArea)
    : '00h 00m',
});

export const getConsecutiveSweeps = (
  reportForCreate: HarmReport | InterventionReport | null,
) => {
  return reportForCreate?.playerDetails?.gambling?.consecutiveSweepsCount?.toString();
};

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;
  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    return new Error(String(maybeError));
  }
}

export function getErrorMessage(error: unknown): string {
  return toErrorWithMessage(error).message;
}
